import { Fragment, useState, type ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { notierungenApi } from '../../api_notierungen'
import { useAktion, useLaden } from '../../hooks'
import Meldungen from '../../komponenten/Meldungen'
import Stammtabelle, { type StammFeld } from '../../komponenten/Stammtabelle'
import { datum, eurT, stueck, tagMonat, zahlLesen, zahlText, zeitpunkt } from '../../format'
import { assistentPfad, notierungLaufPfad, notierungRegelnPfad } from '../../pfade'
import { NOTIERUNG_REGELHERKUNFT, regelBedingungen, regelInWorten } from '../../texte'
import type { NotierungEinstellungen, NotierungLieferant, NotierungQuelle, NotierungRegel, NotierungRegelAktion, NotierungRohstoff } from '../../typen'

/**
 * Stammdaten der Rohstoffnotierungen, je Reiter eine Liste: Spalten (Lieferant × Rohstoff × Qualität
 * × Ladeort, mit Transportzuschlag und der Regel aus den Mails von Frau Geigenmüller), Zuordnungsregeln
 * (welche Position eines Dokuments in welche Spalte gehört), Lieferanten (woran ihre Mails erkannt
 * werden), Rohstoffe (welche Gruppe = welcher Reiter der Tabelle) und die Schwellen der Einkaufssignale.
 */
export default function StammdatenSeite() {
  const { teil } = useParams()
  if (teil === 'regeln') return <Regeln />
  if (teil === 'lieferanten') return <Lieferanten />
  if (teil === 'rohstoffe') return <Rohstoffe />
  if (teil === 'signale') return <Signale />
  return <Spalten />
}

function Spalten() {
  const { daten: quellen, fehler, neuLaden } = useLaden(() => notierungenApi.quellen(), [])
  const { daten: lieferanten } = useLaden(() => notierungenApi.lieferanten(), [])
  const { daten: rohstoffe } = useLaden(() => notierungenApi.rohstoffe(), [])
  // Nach Rohstoff und Reihenfolge, wie die Spalten in der Tabelle stehen.
  const rang = new Map((rohstoffe ?? []).map((r) => [r.id, r.reihenfolge]))
  const sortiert = quellen
    ? [...quellen].sort((a, b) => (rang.get(a.rohstoff_id) ?? 0) - (rang.get(b.rohstoff_id) ?? 0) || a.reihenfolge - b.reihenfolge)
    : null

  const felder: StammFeld<NotierungQuelle>[] = [
    { name: 'rohstoff', titel: 'Rohstoff', nurAnzeige: true },
    { name: 'bezeichnung', titel: 'Spalte', pflicht: true, platzhalter: 'Cargill Rotterdam', zeigen: (q) => <b>{q.bezeichnung}</b> },
    { name: 'lieferant', titel: 'Lieferant', nurAnzeige: true },
    { name: 'lieferant_id', titel: 'Lieferant', art: 'auswahl', alsZahl: true, pflicht: true, nurFormular: true, optionen: (lieferanten ?? []).map((l) => ({ wert: String(l.id), text: l.name })) },
    { name: 'rohstoff_id', titel: 'Rohstoff', art: 'auswahl', alsZahl: true, pflicht: true, nurFormular: true, optionen: (rohstoffe ?? []).map((r) => ({ wert: String(r.id), text: r.name })) },
    { name: 'qualitaet', titel: 'Qualität', platzhalter: 'SG oder MB, leer = egal', nurFormular: true },
    { name: 'ladeort', titel: 'Ladeort (Pflicht)', platzhalter: 'Rotterdam, leer = egal', nurFormular: true },
    { name: 'lieferort', titel: 'gilt für Werk', platzhalter: 'Riesa', nurFormular: true },
    {
      name: 'ladeort',
      titel: 'Qualität · Ladeort · Werk',
      nurAnzeige: true,
      zeigen: (q) => [q.qualitaet && `${q.qualitaet}-Ware`, q.ladeort && `Ladeort ${q.ladeort}`, q.lieferort && `für ${q.lieferort}`].filter(Boolean).join(' · '),
    },
    { name: 'transport_eur', titel: 'Transport €/t', art: 'zahl', platzhalter: '69', zeigen: (q) => (q.transport_eur ? eurT(q.transport_eur) : '') },
    {
      // Nur lesbar: abgeleitet aus den aktiven Regeln dieser Spalte - geändert wird dort.
      name: 'produkt',
      titel: 'Bezeichnungen im Dokument',
      breit: true,
      nurAnzeige: true,
      zeigen: (q) => (
        <>
          {q.produkt.length > 0 ? q.produkt.join(', ') : <span className="hinweis">keine Regel</span>}{' '}
          <Link className="hinweis-link" to={notierungRegelnPfad({ quelle: q.id })} title={`Die Zuordnungsregeln der Spalte ${q.bezeichnung}`}>
            Regeln
          </Link>
        </>
      ),
    },
    { name: 'regel', titel: 'Regel in Worten', breit: true, platzhalter: 'nur Ladeort Rotterdam, Hamburg nie übernehmen' },
    { name: 'reihenfolge', titel: 'Reihenfolge', art: 'zahl', nurFormular: true },
    { name: 'aktiv', titel: 'aktiv', art: 'schalter' },
    {
      name: 'notierungen_anzahl',
      titel: 'Notierungen',
      nurAnzeige: true,
      zeigen: (q) => (
        <>
          {q.notierungen_anzahl.toLocaleString('de-DE')}
          {q.letzte_notierung && <div className="hinweis">zuletzt {datum(q.letzte_notierung)}</div>}
        </>
      ),
    },
  ]

  return (
    <>
      <h2>Spalten</h2>
      <Meldungen fehler={fehler} />
      <Stammtabelle
        einzahl="Spalte"
        zeilen={sortiert}
        felder={felder}
        neu={{ aktiv: true, reihenfolge: 0 }}
        erklaerung="Eine Spalte ist ein Lieferant für einen Rohstoff in einer Qualität und mit Ladeort - wie ein Spaltenkopf der Excel. Welche Bezeichnungen im Dokument zu ihr gehören, steht in den Regeln. Spalten mit Notierungen lassen sich nicht löschen, nur auf „nicht aktiv“ setzen."
        leer="Noch keine Spalte. Sie entstehen auch beim Einlesen der alten Excel-Mappen unter Importe."
        speichern={async (id, werte) => {
          if (id === null) await notierungenApi.quelleAnlegen(werte)
          else await notierungenApi.quelleAendern(id, werte)
          neuLaden()
        }}
        loeschen={async (q) => {
          await notierungenApi.quelleLoeschen(q.id)
          neuLaden()
        }}
      />
    </>
  )
}

const AKTIONEN: { wert: NotierungRegelAktion; text: string }[] = [
  { wert: 'spalte', text: 'in eine Spalte übernehmen' },
  { wert: 'ignorieren', text: 'nicht übernehmen' },
]

/** „… 2 wartende Läufe neu bewertet: Lauf 41, Lauf 42“ - steht hinter „Regel gespeichert.“ */
function neuBewertet(laeufe: number[]): ReactNode {
  if (laeufe.length === 0) return 'Kein wartender Lauf war betroffen.'
  return (
    <>
      {laeufe.length === 1 ? '1 wartender Lauf' : `${laeufe.length} wartende Läufe`} neu bewertet:{' '}
      {laeufe.map((id, i) => (
        <Fragment key={id}>
          {i > 0 && ', '}
          <Link to={notierungLaufPfad(id)}>Lauf {id}</Link>
        </Fragment>
      ))}
    </>
  )
}

/**
 * Die Zuordnungsregeln: „Lieferant X, im Dokument steht Y [, Ladeort enthält Z] [, Qualität Q] → Spalte S“
 * oder „→ nicht übernehmen“. Sie entstehen beim Prüfen eines Laufs („als Regel speichern“), aus den
 * Bezeichnungen der Spalten, von Hand oder über den Assistenten. Gefiltert wird über die Adresse
 * (`?lieferant=`, `?quelle=` von den Spalten, `?regel=` aus dem Lauf), damit Links genau hierher zeigen.
 */
function Regeln() {
  const [suche, setSuche] = useSearchParams()
  const lieferantId = Number(suche.get('lieferant')) || undefined
  const quelleId = Number(suche.get('quelle')) || undefined
  const regelId = Number(suche.get('regel')) || undefined
  const { daten: regeln, fehler, neuLaden } = useLaden(() => notierungenApi.regeln({ lieferant_id: lieferantId, quelle_id: quelleId }), [lieferantId, quelleId])
  const { daten: lieferanten } = useLaden(() => notierungenApi.lieferanten(), [])
  const { daten: quellen } = useLaden(() => notierungenApi.quellen(), [])
  const zeilen = regeln && regelId ? regeln.filter((r) => r.id === regelId) : regeln
  const lieferant = lieferanten?.find((l) => l.id === lieferantId)
  const spalte = quellen?.find((q) => q.id === quelleId)
  const regel = regelId ? regeln?.find((r) => r.id === regelId) : undefined

  function filtern(werte: Record<string, string | null>) {
    const neu = new URLSearchParams(suche)
    for (const [name, wert] of Object.entries(werte)) {
      if (wert) neu.set(name, wert)
      else neu.delete(name)
    }
    setSuche(neu, { replace: true })
  }

  /** Die Spalten eines Lieferanten zur Auswahl - eine inaktive nur, wenn die Regel schon auf ihr steht. */
  function spaltenVon(lieferantWahl: number, gewaehlt: number) {
    return (quellen ?? [])
      .filter((q) => q.lieferant_id === lieferantWahl && (q.aktiv || q.id === gewaehlt))
      .map((q) => ({ wert: String(q.id), text: `${q.rohstoff} · ${q.bezeichnung}${q.aktiv ? '' : ' (nicht aktiv)'}` }))
  }

  const felder: StammFeld<NotierungRegel>[] = [
    { name: 'lieferant', titel: 'Lieferant', nurAnzeige: true },
    { name: 'lieferant_id', titel: 'Lieferant', art: 'auswahl', alsZahl: true, pflicht: true, nurFormular: true, optionen: (lieferanten ?? []).map((l) => ({ wert: String(l.id), text: l.name })) },
    { name: 'produkt', titel: 'Im Dokument steht', pflicht: true, umbruch: true, platzhalter: 'RBD PO SG - ein Teil des Namens genügt', zeigen: (r) => <b>„{r.produkt}“</b> },
    { name: 'ladeort', titel: 'und Ladeort enthält', platzhalter: 'Rotterdam, leer = egal', nurFormular: true },
    { name: 'qualitaet', titel: 'und Qualität', platzhalter: 'SG oder MB, leer = egal', nurFormular: true },
    { name: 'ladeort', titel: 'Bedingungen', nurAnzeige: true, umbruch: true, zeigen: (r) => regelBedingungen(r) },
    { name: 'aktion', titel: 'Wirkung', art: 'auswahl', pflicht: true, nurFormular: true, optionen: AKTIONEN },
    {
      name: 'quelle_id',
      titel: 'Spalte',
      art: 'auswahl',
      alsZahl: true,
      pflicht: true,
      nurFormular: true,
      zeigenWenn: (w) => w.aktion === 'spalte',
      optionen: (w) => spaltenVon(Number(w.lieferant_id), Number(w.quelle_id)),
    },
    {
      name: 'aktion',
      titel: 'Wirkung',
      nurAnzeige: true,
      zeigen: (r) =>
        r.aktion === 'ignorieren' ? (
          <span className="status verworfen">nicht übernehmen</span>
        ) : (
          <>
            → Spalte <b>{r.quelle}</b>
            {r.rohstoff && <div className="hinweis">{r.rohstoff}</div>}
          </>
        ),
    },
    { name: 'grund', titel: 'Grund', breit: true, nurFormular: true, platzhalter: 'Palmöl ab Hamburg kauft die Othüna nicht' },
    {
      // Grund und Herkunft in einer Spalte - sonst wird die Liste breiter als der Bildschirm.
      name: 'grund',
      titel: 'Grund · Herkunft',
      nurAnzeige: true,
      umbruch: true,
      zeigen: (r) => (
        <>
          {r.grund}
          <div className="hinweis" title={`angelegt ${zeitpunkt(r.angelegt_am)}${r.angelegt_von ? ` von ${r.angelegt_von}` : ''}`}>
            {NOTIERUNG_REGELHERKUNFT[r.herkunft]}
          </div>
        </>
      ),
    },
    {
      name: 'treffer',
      titel: 'Treffer',
      art: 'zahl',
      nurAnzeige: true,
      zeigen: (r) => (
        <>
          {stueck(r.treffer)}
          {r.zuletzt_am && (
            <div className="hinweis" title={`zuletzt am ${datum(r.zuletzt_am)}`}>
              zuletzt {tagMonat(r.zuletzt_am)}
            </div>
          )}
        </>
      ),
    },
    { name: 'aktiv', titel: 'aktiv', art: 'schalter', direkt: true },
  ]

  // Die Frage an den Assistenten nimmt mit, worauf die Liste gerade zeigt.
  const worum = regel
    ? ` Es geht um diese Regel: ${regelInWorten(regel)}.`
    : spalte
      ? ` Es geht um die Spalte ${spalte.bezeichnung} (${spalte.rohstoff}).`
      : lieferant
        ? ` Es geht um ${lieferant.name}.`
        : ''
  const frage = `Ich möchte die Zuordnungsregeln der Notierungen bearbeiten.${worum} Zeig mir die Regeln und frag mich, was ich ändern will.`

  return (
    <>
      <h2>Regeln</h2>
      <p className="hinweis" style={{ maxWidth: '80ch' }}>
        Eine Regel sagt, wohin eine Position aus einer Mail oder einem PDF gehört: Lieferant und was im Dokument steht, auf Wunsch
        auch Ladeort oder Qualität → eine Spalte der Tabelle oder „nicht übernehmen“. Nach jeder Änderung werden die wartenden
        Läufe neu zugeordnet.
      </p>
      <div className="leiste felder">
        <label className="feld">
          <span className="hinweis">Lieferant</span>
          <select value={lieferantId ?? ''} onChange={(e) => filtern({ lieferant: e.target.value, quelle: null, regel: null })}>
            <option value="">alle Lieferanten</option>
            {(lieferanten ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
        {quelleId && (
          <span className="filter-marke">
            nur Spalte {spalte ? `${spalte.bezeichnung} (${spalte.rohstoff})` : quelleId}
            <button className="leise" onClick={() => filtern({ quelle: null })}>
              alle Spalten
            </button>
          </span>
        )}
        {regelId && (
          <span className="filter-marke">
            nur Regel {regelId}
            <button className="leise" onClick={() => filtern({ regel: null })}>
              alle Regeln
            </button>
          </span>
        )}
        <Link className="knopf nz-assistent-knopf" to={assistentPfad(frage, 'notierungen')}>
          Mit dem Assistenten bearbeiten
        </Link>
      </div>
      <Meldungen fehler={fehler} />
      <Stammtabelle
        einzahl="Regel"
        zeilen={zeilen}
        felder={felder}
        neu={{ aktiv: true, aktion: 'spalte', lieferant_id: lieferantId ?? spalte?.lieferant_id, quelle_id: quelleId }}
        leer={
          lieferantId || quelleId || regelId
            ? 'Für diese Auswahl gibt es keine Regel. „Regel anlegen“ legt eine an, „alle“ zeigt die übrigen.'
            : 'Noch keine Regel. Regeln entstehen beim Prüfen eines Laufs (Häkchen „als Regel speichern“), aus den Bezeichnungen der Spalten oder hier mit „Regel anlegen“.'
        }
        loeschFrage={(r) => `Diese Regel löschen?\n\n${regelInWorten(r)}\n\nDie wartenden Läufe werden danach neu zugeordnet.`}
        speichern={async (id, werte) => {
          const antwort = id === null ? await notierungenApi.regelAnlegen({ ...werte, herkunft: 'hand' }) : await notierungenApi.regelAendern(id, werte)
          neuLaden()
          return neuBewertet(antwort.neu_bewertet)
        }}
        loeschen={async (r) => {
          await notierungenApi.regelLoeschen(r.id)
          neuLaden()
        }}
      />
    </>
  )
}

function Lieferanten() {
  const { daten, fehler, neuLaden } = useLaden(() => notierungenApi.lieferanten(), [])
  const felder: StammFeld<NotierungLieferant>[] = [
    { name: 'name', titel: 'Name', pflicht: true, platzhalter: 'Cargill', zeigen: (l) => <b>{l.name}</b> },
    { name: 'absender', titel: 'Erkannt an Absender', breit: true, platzhalter: '@cargill.com, preise@olenex.com' },
    { name: 'hinweis', titel: 'Hinweis für das Lesen', breit: true, platzhalter: 'Meldet Palmöl für Rotterdam und Hamburg - nur Rotterdam zählt.' },
    { name: 'aktiv', titel: 'aktiv', art: 'schalter' },
    { name: 'quellen_anzahl', titel: 'Spalten', nurAnzeige: true, zeigen: (l) => String(l.quellen_anzahl) },
  ]
  return (
    <>
      <h2>Lieferanten</h2>
      <Meldungen fehler={fehler} />
      <Stammtabelle
        einzahl="Lieferant"
        zeilen={daten}
        felder={felder}
        neu={{ aktiv: true }}
        erklaerung="Am Absender wird eine Mail diesem Lieferanten und dem Bereich Rohstoffnotierungen zugeordnet - beide Postfächer."
        leer="Noch kein Lieferant angelegt."
        speichern={async (id, werte) => {
          if (id === null) await notierungenApi.lieferantAnlegen({ name: String(werte.name ?? ''), absender: werte.absender ?? null, hinweis: werte.hinweis ?? null })
          else await notierungenApi.lieferantAendern(id, werte)
          neuLaden()
        }}
      />
    </>
  )
}

function Rohstoffe() {
  const { daten, fehler, neuLaden } = useLaden(() => notierungenApi.rohstoffe(), [])
  const felder: StammFeld<NotierungRohstoff>[] = [
    { name: 'name', titel: 'Rohstoff', pflicht: true, platzhalter: 'raff. Palmöl', zeigen: (r) => <b>{r.name}</b> },
    { name: 'gruppe', titel: 'Gruppe (Reiter der Tabelle)', pflicht: true, platzhalter: 'Palmöl · Palmstearin' },
    { name: 'reihenfolge', titel: 'Reihenfolge', art: 'zahl' },
    { name: 'aktiv', titel: 'aktiv', art: 'schalter' },
  ]
  const sortiert = daten ? [...daten].sort((a, b) => a.reihenfolge - b.reihenfolge) : null
  return (
    <>
      <h2>Rohstoffe</h2>
      <Meldungen fehler={fehler} />
      <Stammtabelle
        einzahl="Rohstoff"
        zeilen={sortiert}
        felder={felder}
        neu={{ aktiv: true, reihenfolge: 0 }}
        erklaerung="Rohstoffe derselben Gruppe stehen auf einem Blatt nebeneinander - wie in einer Excel-Mappe."
        leer="Noch kein Rohstoff angelegt."
        speichern={async (id, werte) => {
          if (id === null) await notierungenApi.rohstoffAnlegen(werte)
          else await notierungenApi.rohstoffAendern(id, werte)
          neuLaden()
        }}
      />
    </>
  )
}

const SIGNALFELDER: { name: keyof NotierungEinstellungen; titel: string; einheit: string; erklaerung: string }[] = [
  { name: 'signal_eur', titel: 'Einkaufssignal ab', einheit: '€/t', erklaerung: 'Rückgang zum Vortag, ab dem ein Signal kommt. 10 € sind Rauschen.' },
  { name: 'stark_eur', titel: 'Starkes Signal ab', einheit: '€/t', erklaerung: 'Rückgang an einem Tag, ab dem „heute ist Einkaufstag" gilt.' },
  { name: 'trend_tage', titel: 'Trend über', einheit: 'Notierungstage', erklaerung: 'So viele Tage hintereinander ohne Anstieg …' },
  { name: 'trend_eur', titel: 'Trend ab', einheit: '€/t zusammen', erklaerung: '… und zusammen mindestens so viel günstiger.' },
  { name: 'sprung_prozent', titel: 'Sprung-Sperre ab', einheit: '%', erklaerung: 'Weicht ein Preis stärker vom letzten Wert ab, wird der Lauf nicht von selbst übernommen.' },
]

function Signale() {
  const [texte, setTexte] = useState<Record<string, string> | null>(null)
  const { fehler: ladeFehler } = useLaden(() => notierungenApi.einstellungen(), [], {
    onGeladen: (e) => setTexte(Object.fromEntries(SIGNALFELDER.map((f) => [f.name, zahlText(e[f.name])]))),
  })
  const { fehler, erfolg, arbeitet, setFehler, ausfuehren } = useAktion()

  function speichern() {
    if (!texte) return
    const werte: Partial<NotierungEinstellungen> = {}
    for (const f of SIGNALFELDER) {
      const zahl = zahlLesen(texte[f.name] ?? '')
      if (zahl === null || zahl < 0) {
        setFehler(`${f.titel}: „${texte[f.name]}“ ist keine Zahl.`)
        return
      }
      werte[f.name] = zahl
    }
    void ausfuehren(async () => {
      const neu = await notierungenApi.einstellungenSetzen(werte)
      setTexte(Object.fromEntries(SIGNALFELDER.map((f) => [f.name, zahlText(neu[f.name])])))
    }, 'Gespeichert - die Signale rechnen ab jetzt mit diesen Werten.')
  }

  return (
    <>
      <h2>Signale</h2>
      <p className="hinweis" style={{ maxWidth: '75ch' }}>
        Herr Schulz kauft, wenn ein Preis deutlich fällt: um 15 € über zwei, drei Tage oder um 20 bis 40 € an einem Tag. Die
        Signale stehen nur im Dashboard (Heute, Tabelle), es geht keine Mail hinaus.
      </p>
      <Meldungen fehler={ladeFehler ?? fehler} erfolg={erfolg} />
      {texte && (
        <div className="karte">
          <div className="tabellenrahmen">
            <table className="nz-signale">
              <tbody>
                {SIGNALFELDER.map((f) => (
                  <tr key={f.name}>
                    <td>
                      <label htmlFor={`sig-${f.name}`}>{f.titel}</label>
                    </td>
                    <td>
                      <input
                        id={`sig-${f.name}`}
                        type="text"
                        inputMode="decimal"
                        value={texte[f.name] ?? ''}
                        onChange={(e) => setTexte({ ...texte, [f.name]: e.target.value })}
                      />{' '}
                      <span className="hinweis">{f.einheit}</span>
                    </td>
                    <td className="hinweis">{f.erklaerung}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="leiste" style={{ marginTop: 10, marginBottom: 0 }}>
            <button className="haupt" disabled={arbeitet} onClick={speichern}>
              Speichern
            </button>
          </div>
        </div>
      )}
    </>
  )
}
